import { JobAPI } from "../services/job.service";
import type { Job, JobStatus } from "../types/job";

/**
 * Error thrown when job polling times out
 */
export class JobPollingTimeoutError extends Error {
  constructor(jobId: string, maxDuration: number) {
    super(
      `Job ${jobId} polling timed out after ${maxDuration}ms. The job may still be running on the server.`
    );
    this.name = "JobPollingTimeoutError";
  }
}

/**
 * Error thrown when polling is cancelled via AbortController
 */
export class JobPollingCancelledError extends Error {
  constructor(jobId: string) {
    super(`Job ${jobId} polling was cancelled.`);
    this.name = "JobPollingCancelledError";
  }
}

/**
 * Error thrown when job fails
 */
export class JobFailureError extends Error {
  constructor(jobId: string, errorMessage: string | null) {
    super(errorMessage || `Job ${jobId} failed without error message.`);
    this.name = "JobFailureError";
  }
}

/**
 * Warning class for when detailed_result has expired (Redis TTL)
 */
export class JobDetailedResultExpiredWarning {
  constructor(
    public jobId: string,
    public message: string = "Job completed successfully but detailed results expired. Only metadata is available."
  ) {}
}

/**
 * Progress update callback
 */
export type ProgressCallback = (
  progressPercent: number,
  progressMessage: string | null,
  job: Job
) => void;

/**
 * Polling configuration options
 */
export interface PollJobOptions {
  /** Polling interval in milliseconds (default: 2000) */
  interval?: number;

  /** Maximum polling duration in milliseconds (default: 600000 = 10 minutes) */
  maxDuration?: number;

  /** Maximum number of retry attempts for transient errors (default: 3) */
  maxRetries?: number;

  /** Initial retry delay in milliseconds (default: 1000) */
  initialRetryDelay?: number;

  /** Whether to use exponential backoff for retries (default: true) */
  exponentialBackoff?: boolean;

  /** Callback for progress updates */
  onProgress?: ProgressCallback;

  /** AbortController signal for cancellation */
  signal?: AbortSignal;
}

/**
 * Result returned from successful polling
 */
export interface PollJobResult<TDetailedResult = any, TResult = any> {
  /** The complete job object */
  job: Job<TDetailedResult, TResult>;

  /** The result data (detailed_result if available, otherwise result) */
  data: TDetailedResult | TResult | null;

  /** Warning if detailed_result expired but job succeeded */
  warning?: JobDetailedResultExpiredWarning;
}

/**
 * Sleep utility for delays
 */
const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Polls a job until it reaches SUCCESS or FAILURE status
 *
 * Features:
 * - Configurable polling interval and max duration
 * - Progress callbacks with percent and message
 * - Automatic retry with exponential backoff for transient errors
 * - Cancellation via AbortSignal
 * - Handles Redis TTL expiration gracefully
 * - Returns detailed_result if available, falls back to result
 *
 * @param jobId - The job ID to poll
 * @param options - Polling configuration options
 * @returns Promise resolving to the job result or rejecting on failure/timeout/cancellation
 *
 * @example
 * ```typescript
 * try {
 *   const { data, warning } = await pollJobStatus(jobId, {
 *     onProgress: (percent, message) => console.log(`${percent}% - ${message}`),
 *     signal: abortController.signal,
 *   });
 *
 *   if (warning) {
 *     console.warn(warning.message);
 *   }
 *
 *   console.log('Job completed:', data);
 * } catch (error) {
 *   if (error instanceof JobFailureError) {
 *     console.error('Job failed:', error.message);
 *   } else if (error instanceof JobPollingTimeoutError) {
 *     console.error('Polling timed out');
 *   } else if (error instanceof JobPollingCancelledError) {
 *     console.log('Polling cancelled');
 *   }
 * }
 * ```
 */
export async function pollJobStatus<TDetailedResult = any, TResult = any>(
  jobId: string,
  options: PollJobOptions = {}
): Promise<PollJobResult<TDetailedResult, TResult>> {
  const {
    interval = 2000,
    maxDuration = 600000, // 10 minutes
    maxRetries = 3,
    initialRetryDelay = 1000,
    exponentialBackoff = true,
    onProgress,
    signal,
  } = options;

  const startTime = Date.now();
  let retryCount = 0;

  // Check if already cancelled
  if (signal?.aborted) {
    throw new JobPollingCancelledError(jobId);
  }

  while (true) {
    // Check timeout
    if (Date.now() - startTime > maxDuration) {
      throw new JobPollingTimeoutError(jobId, maxDuration);
    }

    // Check cancellation
    if (signal?.aborted) {
      throw new JobPollingCancelledError(jobId);
    }

    try {
      const { data: job } = await JobAPI.getJob<TDetailedResult, TResult>(
        jobId
      );

      // Call progress callback
      if (onProgress) {
        onProgress(job.progress_percent, job.progress_message, job);
      }

      // Check if job is complete
      if (job.status === "SUCCESS") {
        // Prefer detailed_result, fall back to result
        const data = job.detailed_result ?? job.result;

        // Check if detailed_result expired (TTL)
        const warning =
          !job.detailed_result && job.result
            ? new JobDetailedResultExpiredWarning(jobId)
            : undefined;

        return {
          job,
          data,
          warning,
        };
      }

      if (job.status === "FAILURE") {
        throw new JobFailureError(jobId, job.error_message);
      }

      // Job is still running (PENDING, STARTED, or RETRY)
      // Reset retry count on successful poll
      retryCount = 0;

      // Wait before next poll
      await sleep(interval);
    } catch (error) {
      // If it's one of our custom errors, rethrow immediately
      if (
        error instanceof JobFailureError ||
        error instanceof JobPollingCancelledError ||
        error instanceof JobPollingTimeoutError
      ) {
        throw error;
      }

      // Handle transient network/API errors with retry
      retryCount++;

      if (retryCount > maxRetries) {
        // Max retries exceeded, rethrow the error
        throw new Error(
          `Failed to poll job ${jobId} after ${maxRetries} retries: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }

      // Calculate retry delay with optional exponential backoff
      const retryDelay = exponentialBackoff
        ? initialRetryDelay * Math.pow(2, retryCount - 1)
        : initialRetryDelay;

      // Wait before retrying
      await sleep(retryDelay);
    }
  }
}

/**
 * Type guard to check if an error is a JobPollingTimeoutError
 */
export function isJobPollingTimeoutError(
  error: unknown
): error is JobPollingTimeoutError {
  return error instanceof JobPollingTimeoutError;
}

/**
 * Type guard to check if an error is a JobPollingCancelledError
 */
export function isJobPollingCancelledError(
  error: unknown
): error is JobPollingCancelledError {
  return error instanceof JobPollingCancelledError;
}

/**
 * Type guard to check if an error is a JobFailureError
 */
export function isJobFailureError(error: unknown): error is JobFailureError {
  return error instanceof JobFailureError;
}
